import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpParams, HttpHeaders } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { environment } from 'src/environments/environment';
import { API } from './api';
import { Observable, of } from 'rxjs';
import { AnimationsService } from './animations.service';
import { AuthService } from 'src/app/security/auth.service';

/**
 * Interface para contexto de download de anexos
 * Permite geração automática de nomes de arquivos contextualizados
 */
export interface DownloadContext {
  /** Tipo do contexto (módulo/funcionalidade) */
  contextType: 'ticket' | 'contasPagar' | 'funcionario' | 'aluno' | 'folhaPagamento' | 'ferias' | 'ocorrencia' | 'unidade' | 'fornecedor' | 'generic';
  /** ID do registro relacionado (opcional) */
  contextId?: number | string;
  /** Prefixo customizado para nome do arquivo (opcional) */
  customPrefix?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnexoService {

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private animationsService: AnimationsService,
    private authService: AuthService,
  ) { }

  /**
   * @description Send file to back-end
   * @param file File
   * @returns Observable<any>
   */
  upload(file: FormData) {
    this.animationsService.showProgressBar(true);
    const usuario = this.authService.getToken();
    return this.http.post<any>(`${environment.baseUrl}${API.anexo.cadastrar}`, file, { headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token), reportProgress: true, observe: 'events' })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              // Uploading progress
              const progress = Math.round(100 * event.loaded / event.total);
              return { status: 'progress', progress };
            case HttpEventType.Response:
              // SnackBar success
              this.animationsService.showProgressBar(false);
              this.animationsService.showSuccessSnackBar('Enviado com sucesso');
              return { status: 'done', response: event.body };
            default:
              return;
          }
        }),
        catchError(this.errorHandlerService.errorHandler('Falha ao enviar anexo', []))
      );
  }

  getAnexo(anexo): Observable<any> {
    const usuario = this.authService.getToken();
    return this.http.post(`${environment.baseUrl}${API.anexo.buscarAnexo}`, anexo , {headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token)})
      .pipe(
        catchError(this.errorHandlerService.errorHandler('Falha ao obter os anexos', []))
      );
  }

  deletarAnexo(id: string): Observable<any> {
    this.animationsService.showProgressBar(true);
    const params: HttpParams = new HttpParams().set('idAnexo', id);
    const usuario = this.authService.getToken();
    return this.http.get(`${environment.baseUrl}${API.anexo.deletar}`, { params , headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token)})
      .pipe(
        tap(() => this.animationsService.showProgressBar(false)),
        catchError(this.errorHandlerService.errorHandler('Falha ao deletar anexo', []))
      );
  }

  recusarDocumento(data: any): Observable<any> {
    this.animationsService.showProgressBar(true);
    const usuario = this.authService.getToken();
    return this.http.post(`${environment.baseUrl}${API.anexo.recusarDocumento}`, data , {headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token)})
      .pipe(
        tap(() => this.animationsService.showProgressBar(false)),
        catchError(this.errorHandlerService.errorHandler('Falha ao recusar anexo', []))
      );
  }

  download(id, filename, contentType) {
    this.animationsService.showProgressBar(true);
    const params: HttpParams = new HttpParams().set('idAnexo', id);
    const usuario = this.authService.getToken();
    this.http.get(`${environment.baseUrl}${API.anexo.download}`, { params , headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token)})
      .pipe(
        catchError(this.errorHandlerService.errorHandlerAnexo('Falha ao baixar anexo', []))
      )
      .subscribe(res => {
        this.animationsService.showProgressBar(false);
        if (res?.status === 'error') return;
        const b64Data = res;
        const blob = this.b64toBlob(b64Data, contentType);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        document.body.removeChild(link);
      });
  }

  /**
   * ✅ MÉTODO NOVO (2026-02-09): Download centralizado com geração automática de nomes
   * @description Faz download de anexo com nome de arquivo contextualizado e extensão normalizada
   * @param id ID do anexo
   * @param extensao Extensão do arquivo (com ou sem ponto)
   * @param context Contexto do download (tipo e ID relacionado)
   * @example
   * // Ticket
   * this.anexoService.downloadAnexo(123, 'application/pdf', {
   *   contextType: 'ticket',
   *   contextId: 456
   * });
   * // Resultado: "Ticket-456-Anexo-123.pdf"
   */
  downloadAnexo(id: number, extensao: string, context: DownloadContext): void {
    const filename = this.generateFilename(id, extensao, context);
    this.download(id, filename, extensao); // Reutiliza método legado
  }

  /**
   * @description Gera nome de arquivo contextualizado
   * @param id ID do anexo
   * @param extensao Extensão original (pode conter ponto ou MIME type)
   * @param context Contexto (tipo e ID)
   * @returns Nome do arquivo formatado (ex: "Ticket-123-Anexo-456.pdf")
   */
  private generateFilename(id: number, extensao: string, context: DownloadContext): string {
    const ext = this.normalizeExtension(extensao);
    const prefix = context.customPrefix || this.getContextPrefix(context.contextType);

    if (context.contextId) {
      return `${prefix}-${context.contextId}-Anexo-${id}.${ext}`;
    }
    return `${prefix}-Anexo-${id}.${ext}`;
  }

  /**
   * @description Normaliza extensão removendo ponto inicial e convertendo MIME types
   * @param extensao Extensão original (ex: ".pdf", "application/pdf", "pdf")
   * @returns Extensão limpa (ex: "pdf")
   */
  private normalizeExtension(extensao: string): string {
    if (!extensao) {
      console.warn('⚠️ Extensão não fornecida, usando fallback .bin');
      return 'bin';
    }

    // Remover ponto inicial se existir
    let ext = extensao.startsWith('.') ? extensao.substring(1) : extensao;

    // Converter MIME types comuns para extensões
    const mimeTypeMap: { [key: string]: string } = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'text/plain': 'txt'
    };

    return mimeTypeMap[ext] || ext;
  }

  /**
   * @description Mapeia tipo de contexto para prefixo de nome de arquivo
   * @param contextType Tipo do contexto
   * @returns Prefixo (ex: "Ticket", "ContasPagar")
   */
  private getContextPrefix(contextType: string): string {
    const prefixMap: { [key: string]: string } = {
      ticket: 'Ticket',
      contasPagar: 'ContasPagar',
      funcionario: 'Funcionario',
      aluno: 'Aluno',
      folhaPagamento: 'FolhaPagamento',
      ferias: 'Ferias',
      ocorrencia: 'Ocorrencia',
      unidade: 'Unidade',
      fornecedor: 'Fornecedor',
      generic: 'Anexo'
    };
    return prefixMap[contextType] || 'Anexo';
  }

  imprimir(id, filename, contentType) { //verificar com Nelson
    this.animationsService.showProgressBar(true);
    const params: HttpParams = new HttpParams().set('idAnexo', id);
    const usuario = this.authService.getToken();
    this.http.get(`${environment.baseUrl}${API.anexo.download}`, { params , headers: new HttpHeaders().set('Authorization',  'Bearer '+ usuario?.token)})
      .pipe(
        catchError(this.errorHandlerService.errorHandlerAnexo('Falha ao imprimir anexo', []))
      )
      .subscribe(res => {
        if (res?.status === 'error') return;
        const b64Data = res;
        const blob = this.b64toBlob(b64Data, contentType);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        this.animationsService.showProgressBar(false);
      });
  }


  /**
   * @description Convert a base64 to Blob
   * @param b64Data Base64
   * @param contentType File's type
   * @param sliceSize Slice size
   * @returns Blob
   */
  private b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize ) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }
}
